if (!customElements.get('recipient-form')) {
	customElements.define(
		'recipient-form',
		class RecipientForm extends HTMLElement {
			constructor() {
				super()

				// Keyed by the field names Shopify uses in /cart/add 422
				// errors, so server and client errors share one lookup.
				this.fields = {
					email: this.querySelector(
						`#Recipient-email-${this.dataset.sectionId}`
					),
					message: this.querySelector(
						`#Recipient-message-${this.dataset.sectionId}`
					),
					send_on: this.querySelector(
						`#Recipient-send_on-${this.dataset.sectionId}`
					),
				}
				this.sendOnIso = this.querySelector(
					`#Recipient-send_on-iso-${this.dataset.sectionId}`
				)
				this.offsetProperty = this.querySelector(
					`#Recipient-timezone-offset-${this.dataset.sectionId}`
				)
				if (this.offsetProperty) {
					this.offsetProperty.value = new Date().getTimezoneOffset().toString()
					this.offsetProperty.disabled = false
				}

				this.errorTemplate = this.querySelector(
					`#RecipientForm-errorTemplate-${this.dataset.sectionId}`
				)

				this.fields.send_on.addEventListener(
					'input',
					this.maskSendOn.bind(this)
				)
				this.fields.send_on.addEventListener('blur', () => this.syncSendOn())

				// Clear a field's chip as soon as the user starts editing it,
				// matching the contact form's behaviour.
				Object.values(this.fields).forEach((input) => {
					input.addEventListener('input', () => this.clearError(input))
				})

				// The form carries novalidate, so `required` alone won't stop a
				// submit — validate here and stop the event before Liquid Ajax
				// Cart's listener picks it up. (LAC listens at the document
				// level in the bubble phase, so stopPropagation on the form is
				// sufficient — verified against the bundled build.)
				this.form = this.closest('form')
				if (this.form)
					this.form.addEventListener('submit', this.onSubmit.bind(this))

				this.subscribeToCartRequests()
			}

			onSubmit(evt) {
				const errors = this.validate()
				const keys = Object.keys(errors)
				if (keys.length === 0) return

				evt.preventDefault()
				evt.stopPropagation()
				this.displayErrors(errors)
				const first = this.fields[keys[0]]
				if (first) {
					first.focus({ preventScroll: true })
					/* Guarded: moves the page, and the header's direction detector
					   cannot tell a jump-to-error from a swipe. */
					window.coScrollSafely(() =>
						first.scrollIntoView({ behavior: 'smooth', block: 'center' })
					)
				}
			}

			validate() {
				this.clearAllErrors()
				const errors = {}

				// Same pragmatic format check as the contact form —
				// input.validity accepts "a@b", which bounces at Shopify.
				const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
				const email = this.fields.email.value.trim()
				if (!email) {
					errors.email = this.dataset.errorEmailBlank
				} else if (!EMAIL_RE.test(email)) {
					errors.email = this.dataset.errorEmailInvalid
				}

				const sendOnError = this.syncSendOn()
				if (sendOnError) errors.send_on = sendOnError

				return errors
			}

			// Parses the visible send-on field into both halves: the tidy
			// dd/mm/yyyy display and the ISO value Shopify needs on the
			// hidden property input. Returns the error message, or null.
			syncSendOn() {
				const result = this.parseSendOn()
				if (result.error) {
					this.sendOnIso.value = ''
					return result.error
				}
				this.fields.send_on.value = result.display
				this.sendOnIso.value = result.iso
				return null
			}

			// Typing mask: digits become dd/mm/yyyy as you type. Only ever
			// reformats what was typed, so backspacing works naturally.
			maskSendOn() {
				const input = this.fields.send_on
				const digits = input.value.replace(/\D/g, '').slice(0, 8)
				let masked = digits
				if (digits.length > 4) {
					masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
				} else if (digits.length > 2) {
					masked = `${digits.slice(0, 2)}/${digits.slice(2)}`
				}
				// Keep a just-typed trailing separator so "9/" isn't eaten
				if (/[/.\-\s]$/.test(input.value) && digits.length < 5) masked += '/'
				if (input.value !== masked) input.value = masked
			}

			// Reads the visible field and names exactly what's missing or
			// wrong. A missing year is inferred (nearest future occurrence)
			// rather than rejected. Returns {iso, display} or {error}.
			parseSendOn() {
				const raw = this.fields.send_on.value.trim()
				if (!raw) return { error: this.dataset.errorSendOnBlank }

				const parts = raw.split(/[/.\-\s]+/).filter(Boolean)
				if (parts.some((part) => !/^\d+$/.test(part)))
					return { error: this.dataset.errorSendOnBlank }
				if (parts.length === 1 && parts[0].length > 4) {
					// Slashes skipped entirely, e.g. "25122026"
					const run = parts[0]
					parts.length = 0
					parts.push(run.slice(0, 2), run.slice(2, 4), run.slice(4))
				}
				if (parts.length === 1)
					return { error: this.dataset.errorSendOnAddMonthYear }

				const day = parseInt(parts[0], 10)
				const month = parseInt(parts[1], 10)
				if (day < 1 || day > 31)
					return { error: this.dataset.errorSendOnDayRange }
				if (month < 1 || month > 12)
					return { error: this.dataset.errorSendOnMonthRange }

				const today = new Date()
				today.setHours(0, 0, 0, 0)

				let year
				if (parts.length >= 3) {
					if (parts[2].length !== 2 && parts[2].length !== 4)
						return { error: this.dataset.errorSendOnAddYear }
					year = parseInt(parts[2], 10)
					if (year < 100) year += 2000
				} else {
					// Year left off — take this year, or next if that's passed
					year = today.getFullYear()
					if (new Date(year, month - 1, day) < today) year += 1
				}

				const date = new Date(year, month - 1, day)
				const isRealDate =
					date.getFullYear() === year &&
					date.getMonth() === month - 1 &&
					date.getDate() === day
				if (!isRealDate) return { error: this.dataset.errorSendOnNotReal }

				if (date < today) return { error: this.dataset.errorSendOnPast }
				const max = new Date(today)
				max.setDate(max.getDate() + 90)
				if (date > max) return { error: this.dataset.errorSendOnRange }

				const pad = (n) => String(n).padStart(2, '0')
				return {
					iso: `${year}-${pad(month)}-${pad(day)}`,
					display: `${pad(day)}/${pad(month)}/${year}`,
				}
			}

			// Liquid Ajax Cart owns the /cart/add fetch (see product-form.js).
			// Recipient validation errors come back on that request as a 422
			// with an `errors` object keyed by field, so server error display
			// and the post-add reset both hang off its subscription API.
			subscribeToCartRequests() {
				if (
					typeof liquidAjaxCart === 'undefined' ||
					typeof liquidAjaxCart.subscribeToCartAjaxRequests !== 'function'
				)
					return

				liquidAjaxCart.subscribeToCartAjaxRequests(
					(state, subscribeToResult) => {
						if (state.requestType !== 'add') return
						const initiator = state.info && state.info.initiator
						if (!(initiator instanceof HTMLElement)) return
						if (!initiator.contains(this)) return

						subscribeToResult((state) => {
							if (!state.responseData) return
							if (state.responseData.ok) {
								this.resetRecipientForm()
							} else {
								const body = state.responseData.body || {}
								this.displayServerErrors(body)
							}
						})
					}
				)
			}

			// errors: {fieldKey: message} — one neon chip under each field,
			// same pattern as the contact form. Callers clear first.
			displayErrors(errors) {
				Object.entries(errors).forEach(([key, message]) => {
					const input = this.fields[key]
					if (!input || !this.errorTemplate?.content) return
					const group = input.closest('.recipient-form__group')
					if (!group) return
					const node =
						this.errorTemplate.content.firstElementChild.cloneNode(true)
					const chipId = `RecipientForm-${key}-error-${this.dataset.sectionId}`
					node.id = chipId
					node.querySelector('.recipient-form__error-text').textContent =
						message
					group.appendChild(node)
					input.setAttribute('aria-invalid', 'true')
					input.setAttribute('aria-describedby', chipId)
				})
			}

			// 422 body from /cart/add: {errors: {field: [messages]}} for
			// recipient validation, or a plain description for cart-level
			// problems (those go to the product form's own error slot).
			displayServerErrors(body) {
				this.clearAllErrors()
				if (typeof body.errors === 'object' && body.errors !== null) {
					const errors = {}
					Object.entries(body.errors).forEach(([key, value]) => {
						let message = Array.isArray(value) ? value.join(', ') : value
						if (key === 'email') message = this.dataset.errorEmailInvalid
						errors[key] = message
					})
					this.displayErrors(errors)
					const first = this.fields[Object.keys(errors)[0]]
					/* Guarded: moves the page, and the header's direction detector
					   cannot tell a jump-to-error from a swipe. Braces added — the
					   guard is two lines now, and a brace-less if with a comment
					   between it and its body reads as a bug even when it isn't. */
					if (first) {
						window.coScrollSafely(() =>
							first.scrollIntoView({ behavior: 'smooth', block: 'center' })
						)
					}
					return
				}

				const productForm = this.closest('product-form')
				if (productForm && typeof productForm.handleErrorMessage === 'function')
					productForm.handleErrorMessage(body.description || body.message)
			}

			clearError(input) {
				if (!input.hasAttribute('aria-invalid')) return
				const group = input.closest('.recipient-form__group')
				const chip = group?.querySelector('.recipient-form__error')
				if (chip) chip.remove()
				input.removeAttribute('aria-invalid')
				input.removeAttribute('aria-describedby')
			}

			clearAllErrors() {
				this.querySelectorAll('.recipient-form__error').forEach((chip) =>
					chip.remove()
				)
				Object.values(this.fields).forEach((input) => {
					input.removeAttribute('aria-invalid')
					input.removeAttribute('aria-describedby')
				})
			}

			resetRecipientForm() {
				Object.values(this.fields).forEach((field) => (field.value = ''))
				this.sendOnIso.value = ''
				this.clearAllErrors()
			}
		}
	)
}
